import { Button } from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import { useEffect, useState } from "react";
import PricingRow from "./PricingRow";
import useLoadsPricingStyles from "./LoadsPricingStyles";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";

const LoadPricingConfigEditor = ({
  assetClass,
  ranges,
  onSaveAssetClass,
  clientId
}) => {
  const rubricClasses = useLoadsPricingStyles();


  const [rangesToEdit, setRangesToEdit] = useState([]);
  const [highlightedFields, setHighlightedFields] = useState({});
  const [warningFields, setWarningFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setRangesToEdit(ranges.map(r => ({ ...r })));
  }, [ranges]);


  const highlightNextMin = (index) => {
    setHighlightedFields(prev => ({ ...prev, [index]: true }));
  };

  const autoUpdateNextMin = (list, index, value, highlightFn) => {
    const nextMin = Number(value) + 1;
    if (!isNaN(nextMin) && list[index + 1]) {
      const updated = [...list];
      updated[index + 1] = { ...updated[index + 1], minMiles: nextMin };
      updated[index + 1].id && highlightFn(index + 1);
      return updated;
    }

    return list;
  };

  const handleChange = (index, field, value) => {
    setRangesToEdit(prev => {
      let updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'maxMiles') {
        const min = Number(updated[index].minMiles);
        const max = Number(value);
        setWarningFields(prevWarn => ({
          ...prevWarn,
          [index]: !isNaN(max) && max <= min
        }));
        updated = autoUpdateNextMin(updated, index, value, highlightNextMin);
      }
      return updated;
    });
    setHasChanges(true);
  };


  const handleRemove = (index) => {
    setRangesToEdit(prev => {
      const updated = prev.filter((_, i) => i !== index);
      updated[index] = { ...updated[index], minMiles: prev[index]?.minMiles };
      updated[index] && setHighlightedFields(h => ({ ...h, [index]: true }));

      return updated;
    });
    setHasChanges(true);
  };

  const handleAddClick = () => {
    const lastIndex = rangesToEdit.length - 1;
    const last = lastIndex && rangesToEdit[lastIndex] ? rangesToEdit[lastIndex] : null;

    const newMin = last ? Number(last.maxMiles) + 1 : 1;

    const newRow = {
      minMiles: newMin,
      maxMiles: null,
      pricePerMileDriven: '',
      pricePerMileTowed: '',
      assetClass: assetClass.id,
      clientId: clientId
    };

    setRangesToEdit(prev => [...prev, newRow]);
    setHasChanges(true);
  };

  const hasRate = (value) => value !== '' && value !== null && value !== undefined;

  const validateRanges = () => {
    return rangesToEdit.every((r, idx, arr) => {
      const isLast = idx === arr.length - 1;

      const minValid = r.minMiles !== '' && hasRate(r.pricePerMileDriven) && hasRate(r.pricePerMileTowed);

      const valid = isLast || (
        r.maxMiles !== '' &&
        r.maxMiles !== null &&
        !isNaN(Number(r.maxMiles)) &&
        Number(r.maxMiles) > Number(r.minMiles)
      );
      return minValid && valid;


    });
  };


  const handleSaveAllRanges = () => {
    if (!validateRanges()) {
      return;
    }

    onSaveAssetClass(assetClass, rangesToEdit);
    setHighlightedFields({});
    setHasChanges(false);
  };


  return (
    <div className={rubricClasses.spacing} style={{ width: "100%" }}>
      <table className={rubricClasses.pricingRubricTable} style={{ width: '100%' }}>
        <thead>
          <tr className={rubricClasses.pricingRubricTableHeadRow}>
            <th className={rubricClasses.leftAlign}>From</th>
            <th className={rubricClasses.leftAlign}>To</th>
            <th className={rubricClasses.leftAlign}>Driven $ per mile</th>
            <th className={rubricClasses.leftAlign}>Towed/Hauled $ per mile</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {rangesToEdit.map((range, idx) => (
            <Authorize profile={permissionProfiles.CLIENT_CONFIG.CLIENT_CONFIG_FIELD_EDIT}>
            <PricingRow
              key={idx}
              range={range}
              isLast={rangesToEdit.length - 1 === idx}
              onChange={(field, value) => handleChange(idx, field, value)}
              onRemove={() => handleRemove(idx)}
              disableDelete={rangesToEdit.length < 1}
              classes={rubricClasses}
              highlighted={highlightedFields[idx]}
              warningFields={warningFields[idx]}
            />
            </Authorize>
          )
          )}
        </tbody>
      </table>
            
        <div className={rubricClasses.pricingRubricAddButtonWrapper}>
         <Authorize profile={permissionProfiles.CLIENT_CONFIG.CLIENT_CONFIG_FIELD_EDIT}>
          <Button
            onClick={handleAddClick}
            size="small"
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
          >
            Add Range
          </Button>
          </Authorize>
        </div>

      {hasChanges &&  (
        <div style={{ marginTop: 12 }}>
          <Authorize profile={permissionProfiles.CLIENT_CONFIG.CLIENT_CONFIG_FIELD_EDIT}>
            <Button
              onClick={handleSaveAllRanges}
              size="small"
              color="primary"
              variant="contained"
              style={{ marginRight: 8 }}
              disabled={!validateRanges()}
            >
              Save
            </Button>
          </Authorize>
        </div>
      )}

    </div>
  );
};

export default LoadPricingConfigEditor;